import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { KibButtonNew } from '@chewy/kib-controls-react'
import KibTag from '@chewy/kib-tag-react'
import {
  DateFormat,
  DateUtils,
  HtmlNotesPreview,
  PermissionArea,
  Utils,
} from '@pbt/pbt-ui-components'

import { type ColorVariant, ColorVariantToTheme } from '~/constants/colors'
import DialogNames from '~/constants/DialogNames'
import FeatureToggle from '~/constants/featureToggle'
import useDiagnosisStatus from '~/hooks/useDiagnosisStatus'
import { getCRUDByArea } from '~/store/reducers/auth'
import { getFeatureToggle } from '~/store/reducers/constants'
import type { DiagnosisSnapshotItem } from '~/types'
import { addOriginalBusinessId } from '~/utils'
import useDialog from '~/utils/useDialog'

import ProblemHistoryEnumInfo from './ProblemHistoryEnumInfo'

import '@chewy/kib-tooltip-styles/dist/kib-tooltip.css'
import styles from './RhapProblemHistoryEntry.module.scss'

interface RhapProblemHistoryEntryProps {
  clientId: string
  logs: DiagnosisSnapshotItem[]
  patientId: string
}

const RhapProblemHistoryEntry = ({
  logs,
  clientId,
  patientId,
}: RhapProblemHistoryEntryProps) => {
  const { t } = useTranslation()

  const [openDiagnosisHistoryDialog] = useDialog(DialogNames.DIAGNOSIS_HISTORY)
  const { read: diagnoseReadPermissions } = useSelector(
    getCRUDByArea(PermissionArea.DIAGNOSE),
  )

  const isSingleEntry = logs.length === 1

  const { read: soapReadPermissions } = useSelector(
    getCRUDByArea(PermissionArea.SOAP),
  )
  const isPatientSharingEnabled = useSelector(
    getFeatureToggle(FeatureToggle.PATIENT_SHARING),
  )

  const { activeStatus, statusDisplayName } = useDiagnosisStatus({
    item: logs[0] || {},
    shorten: true,
  })

  const buildSoapUrl = (item: DiagnosisSnapshotItem) =>
    addOriginalBusinessId(
      `/soap/${item.soapId}`,
      isPatientSharingEnabled ? item.businessId : null,
    )

  const handleView = (item?: DiagnosisSnapshotItem) => {
    openDiagnosisHistoryDialog({ clientId, patientId, diagnosis: item })
  }

  const createActionButton = (log: DiagnosisSnapshotItem) => (
    <>
      {log.soapId
        ? soapReadPermissions && (
            <a
              className={styles.actionLink}
              href={buildSoapUrl(log)}
              rel="noreferrer"
              target="_blank"
            >
              {t('Common:VIEW_SOAP')}
            </a>
          )
        : diagnoseReadPermissions && (
            <KibButtonNew
              emphasis="tertiary"
              size="small"
              theme="utility"
              onClick={() => handleView(log)}
            >
              {t('Common:VIEW_ACTION')}
            </KibButtonNew>
          )}
    </>
  )

  return (
    <div className={styles.statusUpdate}>
      <div className={styles.headerRow}>
        <KibTag
          className={styles.statusTag}
          theme={
            ColorVariantToTheme[activeStatus?.colorVariant as ColorVariant]
          }
        >
          {statusDisplayName}
        </KibTag>
        <span className={styles.dateText}>
          {isSingleEntry
            ? DateUtils.formatDate(
                logs[0].date,
                undefined,
                DateFormat.FULL_DATE_YEAR_ABBREV,
              )
            : `${DateUtils.formatDate(logs[logs.length - 1]?.date, undefined, DateFormat.FULL_DATE_YEAR_ABBREV)} - ${DateUtils.formatDate(
                logs[0]?.date,
                undefined,
                DateFormat.FULL_DATE_YEAR_ABBREV,
              )}`}
        </span>
      </div>
      <div className={styles.fullWidthDivider} />

      <div className={styles.logsContainer}>
        {logs.map((log) => (
          <div className={styles.logEntry} key={log.logId || log.id}>
            <div className={styles.logHeader}>
              <div className={styles.dateText}>
                {DateUtils.formatDate(log.date)}
              </div>
              <div className={styles.vetNameText}>
                {Utils.getPersonString(log.vet)}
              </div>
              {createActionButton(log)}
            </div>

            {log.notes && (
              <div className={styles.descriptionText}>
                <HtmlNotesPreview
                  inlineLabel
                  strong
                  fontSize="1.4rem"
                  notes={log.notes}
                  variant="body2"
                />
                <ProblemHistoryEnumInfo log={log} patientId={patientId} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

export default RhapProblemHistoryEntry
